// ANALYSIS AND CHANGES:
// 1. NEW INTERFACE: added the `PaginatedJobApplications` interface to correctly type the API response, which includes `data` and `pagination`.
// 2. TYPING FIXED: `getJobApplicationsWithFilters` now uses `useQuery<PaginatedJobApplications>`. This tells TypeScript the expected response is the paginated object, not a plain array.
// 3. CORRECT DATA EXTRACTION: the logic inside `queryFn` now returns the full `response.data` object, which contains both the application list and the pagination data.
// 4. UNNECESSARY LOGIC REMOVED: the `if/else` that tried to guess the response shape was dropped, since the typing is now explicit and correct.
// RESULT: the hook is now fully aligned with the backend, removing the type errors in `TalentPoolPage` and making pagination work correctly.

import { JobApplication, api } from '@common';
import { useAuth } from '@hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

// Interface for creating a new application
interface CreateJobApplicationInput {
  fullName: string;
  email: string;
  phone: string;
  positionId: string;
  message: string;
}

// Interface for status updates
interface UpdateJobApplicationStatusInput {
  status: 'pending' | 'reviewed' | 'contacted' | 'rejected' | 'hired';
  adminComments?: string;
}

// Interface for the search params
interface JobApplicationsParams {
  page?: number;
  limit?: number;
  search?: string;
  department?: string;
  orderBy?: string;
  orderType?: 'asc' | 'desc';
}

// NOVA INTERFACE: Define a estrutura da resposta paginada da API.
export interface PaginatedJobApplications {
  data: JobApplication[];
  pagination: {
    totalCount: number;
    page: number;
    pageSize: number;
  };
}

// Fetches applications with filters and pagination
export const getJobApplicationsWithFilters = (params: JobApplicationsParams) => {
  const { isAuthenticated } = useAuth();

  // FIX: the useQuery typing now reflects the paginated API response.
  return useQuery<PaginatedJobApplications>({
    queryKey: ['jobApplications', params],
    queryFn: async () => {
      const response = await api.get('/job-applications', {
        params: {
          ...params,
          include: 'position', // Makes sure the position data comes along
        },
      });
      // Return the full object: { data: [...], pagination: {...} }
      return response.data;
    },
    enabled: isAuthenticated,
  });
};


export const useJobApplications = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  // This query fetches every application without pagination; kept for other uses
  const jobApplications = useQuery<JobApplication[]>({
    queryKey: ['jobApplications', 'all'], // Distinct key so it does not collide
    queryFn: async () => {
      const response = await api.get('/job-applications');
      // The API may return the paginated object even without params, so make sure we return only the array
      return response.data.data || response.data;
    },
    enabled: isAuthenticated
  });

  const getJobApplicationsByPositionId = (positionId: string) => {
    return useQuery<JobApplication[]>({
      queryKey: ['jobApplications', 'position', positionId],
      queryFn: () => api.get(`/job-applications/position/${positionId}`).then((res) => res.data),
      enabled: !!positionId && isAuthenticated
    });
  };

  const getJobApplicationById = (id: string) => {
    return useQuery<JobApplication>({
      queryKey: ['jobApplication', id],
      queryFn: () => api.get(`/job-applications/${id}`).then((res) => res.data),
      enabled: !!id && isAuthenticated
    });
  };

  const createJobApplication = useMutation({
    mutationFn: async ({ data, resume }: { data: CreateJobApplicationInput; resume?: File }) => {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => formData.append(key, value));
      if (resume) formData.append('resume', resume);

      const response = await api.post('/job-applications', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobApplications'] });
    }
  });

  const updateJobApplicationStatus = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateJobApplicationStatusInput }) => {
      return api.patch(`/job-applications/${id}/status`, data).then((res) => res.data);
    },
    onSuccess: (_data, variables) => {
      message.success('Application status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['jobApplications'] });
      queryClient.invalidateQueries({ queryKey: ['jobApplication', variables.id] });
    }
  });

  const deleteJobApplication = useMutation({
    mutationFn: (id: string) => api.delete(`/job-applications/${id}`).then((res) => res.data),
    onSuccess: () => {
      message.success('Application deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['jobApplications'] });
    }
  });

  const getResumeUrl = (resume: string) => {
    return `${import.meta.env.VITE_URL_FILES}/${resume}`;
  };

  return {
    jobApplications,
    getJobApplicationsByPositionId,
    getJobApplicationById,
    createJobApplication,
    updateJobApplicationStatus,
    deleteJobApplication,
    getResumeUrl
  };
};
